import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { closeSearchModal, openOrFocusTicketTab, openSearchModal } from '../../../../../../actions/ui';
import { SearchResultsList } from '../../../../pages/search/components';
import { useGlobalSearch } from '../../../../pages/search/hooks';

const FILTER_OPTIONS = [
    { value: 'all', label: 'Todos' },
    { value: 'ticket', label: 'Tickets' },
    { value: 'customer', label: 'Clientes' },
    { value: 'project', label: 'Proyectos' },
    { value: 'user', label: 'Usuarios' },
];

export const SearchAction = ({ onToggle, onClose }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { token } = useSelector((state) => state.auth);
    const { searchModalOpen } = useSelector((state) => state.ui);
    const dialogRef = React.useRef(null);
    const inputRef = React.useRef(null);

    const {
        query,
        setQuery,
        activeFilter,
        setActiveFilter,
        results,
        loading,
        error,
        totalCount,
    } = useGlobalSearch({ token });

    const handleClose = React.useCallback(() => {
        dispatch(closeSearchModal());
        onClose?.();
    }, [dispatch, onClose]);

    const handleOpen = React.useCallback(() => {
        onToggle?.();
        dispatch(openSearchModal());
    }, [dispatch, onToggle]);

    React.useEffect(() => {
        if (!searchModalOpen) {
            return undefined;
        }

        const focusTimer = window.setTimeout(() => {
            inputRef.current?.focus();
        }, 10);

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };

        const handleClickOutside = (event) => {
            if (!dialogRef.current?.contains(event.target)) {
                handleClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.clearTimeout(focusTimer);
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [searchModalOpen, handleClose]);

    const handleOpenFullResults = React.useCallback(() => {
        const normalizedQuery = String(query || '').trim();
        if (normalizedQuery.length < 2) {
            return;
        }

        handleClose();
        navigate(`/search?q=${encodeURIComponent(normalizedQuery)}&type=${encodeURIComponent(activeFilter || 'all')}`);
    }, [query, activeFilter, handleClose, navigate]);

    const handleSearchSubmit = React.useCallback((event) => {
        event.preventDefault();
        handleOpenFullResults();
    }, [handleOpenFullResults]);

    const handleSelectTicket = React.useCallback((ticket) => {
        if (!ticket?._id) {
            return;
        }

        dispatch(openOrFocusTicketTab({
            id: ticket._id,
            code: ticket.code,
            title: ticket.title,
        }));
        handleClose();
        navigate(`/tasks/detail/${ticket._id}`, {
            state: { ticketCode: ticket.code },
        });
    }, [dispatch, handleClose, navigate]);

    const handleSelectCustomer = React.useCallback((customer) => {
        if (!customer?._id) {
            return;
        }

        handleClose();
        navigate(`/customer/detail/${customer._id}`);
    }, [handleClose, navigate]);

    const handleSelectProject = React.useCallback((project) => {
        if (!project?._id) {
            return;
        }

        handleClose();
        navigate(`/project/detail/${project._id}`);
    }, [handleClose, navigate]);

    const handleSelectUser = React.useCallback((user) => {
        if (!user?._id) {
            return;
        }

        handleClose();
        navigate('/settings/users', {
            state: {
                highlightUserId: user._id,
            },
        });
    }, [handleClose, navigate]);

    return (
        <div className="nxl-h-item nxl-header-search">
            <button
                type="button"
                className="nxl-head-link me-0 border-0 bg-transparent p-0"
                onClick={handleOpen}
            >
                <i className="feather-search" />
            </button>

            {searchModalOpen ? (
                <div className="search-modal__backdrop" role="presentation">
                    <div
                        ref={dialogRef}
                        className="search-modal__dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Búsqueda global"
                    >
                        <form className="search-modal__form" onSubmit={handleSearchSubmit}>
                            <div className="search-modal__search-shell">
                                <i className="feather-search search-modal__search-icon" aria-hidden="true" />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    className="search-modal__input"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder="Buscar por código, nombre, correo o título..."
                                />
                                <button type="button" className="search-modal__esc" onClick={handleClose}>
                                    ESC
                                </button>
                            </div>
                        </form>

                        <div className="search-modal__chips">
                            {FILTER_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`search-modal__chip ${activeFilter === option.value ? 'is-active' : ''}`}
                                    onClick={() => setActiveFilter(option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>

                        <div className="search-modal__body">
                            <SearchResultsList
                                query={query}
                                loading={loading}
                                error={error}
                                results={results}
                                totalCount={totalCount}
                                showSectionCounts={false}
                                onSelectTicket={handleSelectTicket}
                                onSelectCustomer={handleSelectCustomer}
                                onSelectProject={handleSelectProject}
                                onSelectUser={handleSelectUser}
                            />
                        </div>

                        <div className="search-modal__footer">
                            <button
                                type="button"
                                className="btn btn-light-brand"
                                onClick={handleOpenFullResults}
                                disabled={String(query || '').trim().length < 2}
                            >
                                Ver todos los resultados
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
